import {
  getPlaceId,
  placeHasAddressComponents,
} from './parseGooglePlaceAddress.js';

export const PLACE_ADDRESS_FIELDS = [
  'address_components',
  'formatted_address',
  'place_id',
  'name',
];

const NEW_PLACE_ADDRESS_FIELDS = [
  'addressComponents',
  'formattedAddress',
  'id',
  'displayName',
];

function getGoogleMaps(google = globalThis.google ?? globalThis.window?.google) {
  return google?.maps?.places ? google : null;
}

/**
 * When Autocomplete fires `place_changed` before Place Details arrive, `getPlace()`
 * often only has a name/place_id. Fetch components so city / province / postal
 * can be filled on the first selection.
 *
 * @param {object} place
 * @param {{ google?: object }} [deps]
 * @returns {Promise<object>}
 */
export async function ensurePlaceAddressDetails(place, deps = {}) {
  if (placeHasAddressComponents(place)) return place;

  const google = getGoogleMaps(deps.google);
  const placeId = getPlaceId(place);
  if (!google || !placeId) return place;

  if (typeof place?.fetchFields === 'function') {
    try {
      await place.fetchFields({ fields: NEW_PLACE_ADDRESS_FIELDS });
      if (placeHasAddressComponents(place)) return place;
    } catch {
      // Fall through to PlacesService / Place.
    }
  }

  if (typeof google.maps.places.PlacesService === 'function') {
    const details = await new Promise((resolve) => {
      try {
        const attr =
          typeof document !== 'undefined'
            ? document.createElement('div')
            : { nodeType: 1 };
        const service = new google.maps.places.PlacesService(attr);
        service.getDetails(
          { placeId, fields: PLACE_ADDRESS_FIELDS },
          (result, status) => {
            const ok =
              status === google.maps.places.PlacesServiceStatus?.OK ||
              status === 'OK';
            resolve(ok && result ? result : null);
          },
        );
      } catch {
        resolve(null);
      }
    });
    if (details && placeHasAddressComponents(details)) return details;
  }

  if (typeof google.maps.places.Place === 'function') {
    try {
      const next = new google.maps.places.Place({ id: placeId });
      await next.fetchFields({ fields: NEW_PLACE_ADDRESS_FIELDS });
      if (placeHasAddressComponents(next)) return next;
    } catch {
      return place;
    }
  }

  return place;
}

/**
 * Stable key so Autocomplete is not torn down when only the React callback identity changes.
 * Recreating the widget on every keystroke drops the first `place_changed` (full address
 * stays in the street field; city / postal / province stay empty until a second pick).
 */
export function autocompleteOptionsKey(options = {}) {
  const { enabled = true, componentRestrictions, types, fields } = options;
  return JSON.stringify({
    enabled: enabled !== false,
    componentRestrictions: componentRestrictions || null,
    types: types || ['address'],
    fields: fields || PLACE_ADDRESS_FIELDS,
  });
}
